import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, defer, tap, throwError } from 'rxjs';

export type TelemetryAttributeValue = string | number | boolean | null | undefined;
export type TelemetryAttributes = Record<string, TelemetryAttributeValue>;
export type TelemetryMeasurements = Record<string, number>;

interface TrackOperationOptions<T> {
  successEventName: string;
  failureEventName?: string;
  attributes?: TelemetryAttributes;
  successAttributes?: (value: T) => TelemetryAttributes;
  measurements?: TelemetryMeasurements;
}

@Injectable({ providedIn: 'root' })
export class AppTelemetryService {
  private currentRouteTemplate = '/';
  private currentViewAttributes: TelemetryAttributes = {};

  trackView(routeTemplate: string, attributes: TelemetryAttributes = {}): void {
    this.currentRouteTemplate = routeTemplate;
    this.currentViewAttributes = {
      routeTemplate,
      ...attributes,
    };

    const normalizedAttributes = this.normalizeAttributes({
      routeTemplate,
      ...attributes,
    });
    this.trackEvent('view.changed', normalizedAttributes);
  }

  getCurrentViewAttributes(): TelemetryAttributes {
    return {
      routeTemplate: this.currentRouteTemplate,
      ...this.currentViewAttributes,
    };
  }

  trackEvent(
    eventName: string,
    attributes: TelemetryAttributes = {},
    measurements?: TelemetryMeasurements,
  ): void {
    this.normalizeAttributes({
      eventName,
      ...attributes,
      ...measurements,
    });
    this.normalizeMeasurements(measurements);
  }

  trackOperation<T>(
    operation: Observable<T>,
    options: TrackOperationOptions<T>,
  ): Observable<T> {
    return defer(() => {
      const startedAt = performance.now();

      return operation.pipe(
        tap(value => {
          const durationMs = Math.round(performance.now() - startedAt);
          this.trackEvent(
            options.successEventName,
            {
              ...options.attributes,
              ...options.successAttributes?.(value),
              statusCode: 200,
            },
            {
              ...options.measurements,
              durationMs,
            },
          );
        }),
        catchError((error: unknown) => {
          const durationMs = Math.round(performance.now() - startedAt);
          this.trackEvent(
            options.failureEventName ?? `${options.successEventName}.failed`,
            {
              ...options.attributes,
              statusCode: this.getStatusCode(error),
            },
            {
              ...options.measurements,
              durationMs,
            },
          );

          return throwError(() => error);
        }),
      );
    });
  }

  private normalizeAttributes(attributes: TelemetryAttributes): Record<string, string> {
    return Object.entries(attributes).reduce<Record<string, string>>((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = String(value);
      }

      return acc;
    }, {});
  }

  private normalizeMeasurements(measurements?: TelemetryMeasurements): TelemetryMeasurements | undefined {
    if (!measurements) {
      return undefined;
    }

    const normalized = Object.entries(measurements).reduce<TelemetryMeasurements>((acc, [key, value]) => {
      if (Number.isFinite(value)) {
        acc[key] = value;
      }

      return acc;
    }, {});

    return Object.keys(normalized).length > 0 ? normalized : undefined;
  }

  private getStatusCode(error: unknown): number | string {
    if (error instanceof HttpErrorResponse) {
      return error.status || 'network_error';
    }

    return 'client_error';
  }
}
